import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TournamentStatusPill } from "../components/TournamentStatusPill";
import { useSetTrail } from "../context/BreadcrumbContext";
import { api } from "../lib/api";
import type { TournamentSummary } from "../types/domain";

function TournamentCardSkeleton() {
  return (
    <div className="card animate-pulse">
      <div className="flex items-start justify-between gap-3">
        <div className="h-4 w-40 rounded bg-gray-200" />
        <div className="h-5 w-20 rounded-full bg-gray-100" />
      </div>
      <div className="mt-1 h-3 w-64 rounded bg-gray-100" />
      <div className="mt-3 flex flex-wrap gap-2">
        <div className="h-4 w-16 rounded-full bg-gray-100" />
        <div className="h-4 w-20 rounded-full bg-gray-100" />
      </div>
    </div>
  );
}

export function TournamentsPage() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useSetTrail([]);

  useEffect(() => {
    void api.listTournaments().then((data) => {
      setTournaments(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-ink">Tournois</h1>
        {user?.role === "admin" && (
          <Link to="/admin/tournaments/new" className="btn-primary">
            + Créer un tournoi
          </Link>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <TournamentCardSkeleton key={i} />
          ))}
        </div>
      ) : tournaments.length > 0 ? (
        <div className="space-y-3">
          {tournaments.map((t) => (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              className="card block no-underline transition hover:border-ember/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-ink">{t.name}</div>
                <span className="shrink-0">
                  <TournamentStatusPill status={t.status} />
                </span>
              </div>
              <p className="mt-1 text-sm text-smoke">{t.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-smoke">
                  {t.teamCount} équipes
                </span>
                {t.ruleHighlights.map((rule) => (
                  <span
                    key={rule}
                    className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-smoke"
                  >
                    {rule}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-xs font-semibold text-ember">
                Voir le tournoi &rarr;
              </p>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-smoke">Aucun tournoi disponible.</p>
      )}
    </div>
  );
}
